import { useEffect, useState } from 'react';
import { Box, FormControl, FormLabel, Select, Option, Typography, Chip, Alert, Button } from '@mui/joy';
import { Phone as PhoneIcon } from '@phosphor-icons/react';
import { useUserPhoneNumbers } from '../../hooks/useUserPhoneNumbers';
import type { UserPhoneNumberOption } from '../../types/inbox';

interface PhoneNumberSelectorProps {
  value?: string;
  onChange: (phoneNumber: string) => void;
  smsOnly?: boolean;
  voiceOnly?: boolean;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

/**
 * Dropdown component for selecting a phone number from user's assigned numbers.
 * - Auto-selects the user's default number on load
 * - Shows single number as static text (no dropdown needed)
 * - Returns null if no numbers are assigned (graceful fallback)
 */
export const PhoneNumberSelector = ({
  value,
  onChange,
  smsOnly = false,
  voiceOnly = false,
  label = 'From',
  size = 'sm',
  disabled = false,
}: PhoneNumberSelectorProps) => {
  const { phoneNumbers, defaultNumber, loading, hasPhoneNumbers, error, refetch } = useUserPhoneNumbers({
    smsOnly,
    voiceOnly,
  });
  const [initialized, setInitialized] = useState(false);

  // Auto-select default number on first load
  useEffect(() => {
    if (!initialized && !loading && hasPhoneNumbers && !value) {
      if (defaultNumber && (!smsOnly || defaultNumber.sms_ready)) {
        onChange(defaultNumber.phone_number);
      } else if (phoneNumbers.some(number => !smsOnly || number.sms_ready)) {
        onChange(phoneNumbers.find(number => !smsOnly || number.sms_ready)!.phone_number);
      } else return;
      setInitialized(true);
    }
  }, [initialized, loading, hasPhoneNumbers, value, defaultNumber, phoneNumbers, onChange]);

  if (error) return <Alert color="warning" endDecorator={<Button onClick={refetch}>Retry</Button>}>{error}</Alert>;
  if (!loading && !hasPhoneNumbers) return <Alert color="warning">No assigned {smsOnly ? 'texting' : 'calling'} number. Ask an admin to assign a number in Settings → Communications.</Alert>;
  if (smsOnly && phoneNumbers.length === 1 && !phoneNumbers[0].sms_ready) return <Alert color="warning">{phoneNumbers[0].readiness_reason || 'This number is not ready to text.'}</Alert>;

  // If only one number, show as static text
  if (phoneNumbers.length === 1) {
    const number = phoneNumbers[0];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          {label}:
        </Typography>
        <Chip
          size={size}
          variant="soft"
          color="primary"
          startDecorator={<PhoneIcon size={14} />}
        >
          {formatPhoneDisplay(number)}
        </Chip>
      </Box>
    );
  }

  return (
    <FormControl size={size}>
      <FormLabel>{label}</FormLabel>
      <Select
        value={value || ''}
        onChange={(_, newValue) => newValue && onChange(newValue)}
        startDecorator={<PhoneIcon size={16} />}
        disabled={disabled || loading}
        placeholder={loading ? 'Loading...' : 'Select phone number'}
        sx={{ minWidth: 200 }}
      >
        {phoneNumbers.map((number) => (
          <Option key={number.phone_number} value={number.phone_number} disabled={smsOnly && !number.sms_ready}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body-sm">{formatPhoneDisplay(number)}{smsOnly && !number.sms_ready ? ` — ${number.readiness_reason || "Not ready"}` : ""}</Typography>
              {number.is_default && (
                <Chip size="sm" variant="soft" color="primary">
                  Default
                </Chip>
              )}
            </Box>
          </Option>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Format phone number for display.
 * Shows friendly name if available, otherwise formats the phone number.
 */
function formatPhoneDisplay(number: UserPhoneNumberOption): string {
  if (number.friendly_name) {
    return `${number.friendly_name} (${formatPhoneNumber(number.phone_number)})`;
  }
  return formatPhoneNumber(number.phone_number);
}

/**
 * Format E.164 phone number to human-readable format.
 */
function formatPhoneNumber(phone: string): string {
  // Handle US numbers (+1XXXXXXXXXX)
  if (phone.startsWith('+1') && phone.length === 12) {
    const digits = phone.slice(2);
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  // Return as-is for other formats
  return phone;
}

export default PhoneNumberSelector;
